import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, Check, Plus, Trash2, Pencil, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "../../App";
import { useT } from "../../lib/i18n";
import { compressImage } from "../../lib/compressImage";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIME_SLOTS = [
  { id: "morning", label: "Morning", time: "8-12" },
  { id: "midday", label: "Midday", time: "12-15" },
  { id: "afternoon", label: "Afternoon", time: "15-18" },
  { id: "evening", label: "Evening", time: "18+" }
];
// Same curated lists the admin Schedule tab uses, so guests pick consistent emojis + locations.
import { EVENT_CATEGORIES, CategoryTags, CategoryPicker, EmojiPicker, emojiForCategories } from "./eventCategories";
const EVENT_LOCATIONS = ["Living Room", "Dining Room", "Kitchen", "Pool Area", "Garden", "Terrace", "Driveway", "Gym", "Sierra Calderona"];

const resolveImg = (u) => (u?.startsWith("/") ? `${API.replace(/\/api$/, "")}${u}` : u);

const getMonday = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  monday.setHours(0, 0, 0, 0);
  return monday;
};

function pickTimeSlot(timeStr) {
  if (!timeStr) return "morning";
  const h = parseInt(timeStr.split(":")[0], 10);
  if (Number.isNaN(h)) return "morning";
  if (h < 12) return "morning";
  if (h < 15) return "midday";
  if (h < 18) return "afternoon";
  return "evening";
}

const ActivitiesTab = ({ weeklySchedule, espressoTalks, headers, onRefresh, communityProfiles = [] }) => {
  const { user } = useAuth();
  const isRetreat = user?.guest_type === "retreat";
  const { t, locale } = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedWeekStart, setSelectedWeekStart] = useState(getMonday);
  const [rsvpBusyId, setRsvpBusyId] = useState(null);
  const [eventSheet, setEventSheet] = useState(null); // { activity, attendees, loading }

  // ---- create event flow ----
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayISO = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const fileInputRef = useRef(null);
  const [newEvent, setNewEvent] = useState({
    name: "",
    emoji: "🎉",
    date: todayISO,
    time: "18:00",
    time_slot: "evening",
    location: "",
    description: "",
    images: [],
    categories: [],
    co_host_id: "",
  });
  const [overlapPrompt, setOverlapPrompt] = useState(null); // { events: [...] }
  // Mobile portrait: which day index (0-6) the user is viewing
  const [mobileDay, setMobileDay] = useState(() => {
    const d = new Date().getDay();
    return d === 0 ? 6 : d - 1; // Mon=0, Sun=6
  });

  const resetEventForm = () => {
    setNewEvent({
      name: "", emoji: "🎉", date: todayISO, time: "18:00",
      time_slot: "evening", location: "", description: "", images: [], categories: [], co_host_id: "",
    });
    setEditingId(null);
    setEmojiTouched(false);
  };

  const [emojiTouched, setEmojiTouched] = useState(false);
  const [catFilter, setCatFilter] = useState("all");

  const setCategories = (cats) => {
    if (cats.length > 3) return;
    setNewEvent((p) => ({ ...p, categories: cats, emoji: emojiTouched ? p.emoji : emojiForCategories(cats) }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 2 - newEvent.images.length);
    e.target.value = "";
    if (files.length === 0) return;
    setUploadingImg(true);
    try {
      const urls = [];
      for (const f of files) {
        const compressed = await compressImage(f);
        const fd = new FormData();
        fd.append("file", compressed);
        const res = await axios.post(`${API}/upload/image`, fd, { headers });
        urls.push(res.data.url);
      }
      setNewEvent((prev) => ({ ...prev, images: [...prev.images, ...urls].slice(0, 2) }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't upload that photo");
    } finally {
      setUploadingImg(false);
    }
  };

  const submitNewEvent = async (forceThroughOverlap = false) => {
    if (!newEvent.name?.trim() || !newEvent.date || !newEvent.time) {
      toast.error("Name, date and time are required");
      return;
    }
    setCreating(true);
    try {
      if (!forceThroughOverlap && !editingId) {
        const ov = await axios.get(
          `${API}/community/check-overlap?date=${newEvent.date}&time=${newEvent.time}`,
          { headers },
        );
        if (ov.data?.has_overlap) {
          setOverlapPrompt({ events: ov.data.overlapping_events || [] });
          setCreating(false);
          return;
        }
      }
      const payload = {
        name: newEvent.name.trim(),
        emoji: newEvent.emoji || "🎉",
        date: newEvent.date,
        time_slot: newEvent.time_slot || pickTimeSlot(newEvent.time),
        time: newEvent.time,
        location: newEvent.location || null,
        description: newEvent.description || null,
        images: newEvent.images.slice(0, 2),
        recurring: false,
        categories: newEvent.categories.slice(0, 3),
        co_host_id: newEvent.co_host_id || null,
      };
      if (payload.categories.length === 0) {
        toast.error(t("Pick at least one category"));
        setCreating(false);
        return;
      }
      if (editingId) {
        await axios.put(`${API}/community/events/${editingId}`, payload, { headers });
        toast.success("Event updated ✅");
      } else {
        const res = await axios.post(`${API}/community/events`, payload, { headers });
        if (res.data?.event?.status === "pending") {
          toast.success("Sent to Steve for approval 🙏 The house gets invited once it's approved");
        } else {
          toast.success("Event added 🎉 The whole house has been invited");
        }
      }
      setShowAdd(false);
      setOverlapPrompt(null);
      resetEventForm();
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (editingId ? "Couldn't save your changes" : "Couldn't add the event"));
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (activity) => {
    setNewEvent({
      name: activity.name || "",
      emoji: activity.emoji || "🎉",
      date: activity.date || todayISO,
      time: activity.time || "18:00",
      time_slot: activity.time_slot || pickTimeSlot(activity.time),
      location: activity.location || "",
      description: activity.description || "",
      images: (activity.images || []).slice(0, 2),
      categories: (activity.categories || []).slice(0, 3),
      co_host_id: activity.co_host_id || "",
    });
    setEmojiTouched(true);
    setEditingId(activity.id);
    setEventSheet(null);
    setShowAdd(true);
  };

  const approvePendingEvent = async (eventId, approve) => {
    try {
      await axios.post(`${API}/admin/events/${eventId}/${approve ? "approve" : "deny"}`, {}, { headers });
      toast.success(approve ? "Approved — the house has been invited 🎉" : "Declined — the creator has been notified");
      setEventSheet(null);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the event");
    }
  };

  const deleteMyEvent = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/community/events/${eventId}`, { headers });
      toast.success("Event removed");
      setEventSheet(null);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  const toggleRSVP = async (activity) => {
    if (!headers) return;
    setRsvpBusyId(activity.id);
    try {
      const res = await axios.post(
        `${API}/community/events/rsvp`,
        { occurrence_key: activity.id, going: !activity.i_am_going },
        { headers }
      );
      toast.success(activity.i_am_going ? "Removed from your list" : "You're going! 🙌");
      // Sync the open sheet without re-fetching the entire schedule
      if (eventSheet && eventSheet.activity.id === activity.id) {
        setEventSheet((prev) => prev ? {
          ...prev,
          activity: { ...prev.activity, i_am_going: !activity.i_am_going, attendee_count: res.data?.attendee_count ?? prev.activity.attendee_count },
        } : prev);
        // Refresh attendee list to reflect the change
        loadAttendees(activity.id);
      }
      onRefresh?.();
    } catch (error) {
      toast.error("Couldn't update RSVP");
    } finally {
      setRsvpBusyId(null);
    }
  };

  const loadAttendees = async (occurrenceKey) => {
    try {
      const res = await axios.get(`${API}/community/events/${encodeURIComponent(occurrenceKey)}/attendees`, { headers });
      setEventSheet((prev) => prev ? { ...prev, attendees: res.data?.attendees || [], loading: false } : prev);
    } catch (error) {
      toast.error("Couldn't load attendees");
    }
  };

  const openEventDetail = async (activity) => {
    setEventSheet({ activity, attendees: [], loading: true });
    loadAttendees(activity.id);
  };

  // Deep-link: /community?tab=activities&event=<id> jumps to the event's day and opens it
  useEffect(() => {
    const evId = searchParams.get("event");
    if (!evId || weeklySchedule.length === 0) return;
    const match = weeklySchedule.find((a) => a.id === evId);
    if (match?.date) {
      const [y, m, d] = match.date.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      const dow = dt.getDay();
      const monday = new Date(dt);
      monday.setDate(dt.getDate() - (dow === 0 ? 6 : dow - 1));
      monday.setHours(0, 0, 0, 0);
      setSelectedWeekStart(monday);
      setMobileDay(dow === 0 ? 6 : dow - 1);
      openEventDetail(match);
    } else {
      toast.info("That event isn't on the calendar anymore");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("event");
    setSearchParams(next, { replace: true });
  }, [weeklySchedule]); // eslint-disable-line react-hooks/exhaustive-deps

  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(selectedWeekStart);
      date.setDate(selectedWeekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const dateStrFor = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const matchesFilter = (a) => catFilter === "all" || (a.categories || []).includes(catFilter);

  const getActivitiesForDateSlot = (date, timeSlot) =>
    weeklySchedule.filter(a => a.date === dateStrFor(date) && a.time_slot === timeSlot && matchesFilter(a));

  const getActivitiesForDate = (date) =>
    weeklySchedule.filter(a => a.date === dateStrFor(date) && matchesFilter(a));

  const isToday = (date) => date.toDateString() === new Date().toDateString();

  const goToPreviousWeek = () => {
    const newStart = new Date(selectedWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setSelectedWeekStart(newStart);
  };
  const goToNextWeek = () => {
    const newStart = new Date(selectedWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setSelectedWeekStart(newStart);
  };
  const goToThisWeek = () => setSelectedWeekStart(getMonday());

  const formatDateHeader = (date) =>
    date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });

  const formatWeekRange = () => {
    const weekEnd = new Date(selectedWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return `${selectedWeekStart.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} - ${weekEnd.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  const weekDates = getWeekDates();

  return (
    <div className="space-y-2 sm:space-y-4">
      {/* Calendar Header — slim strip with week nav and quick actions */}
      <div className="bg-white border border-stone-200 shadow-sm shadow-stone-200/50 rounded-xl sm:rounded-2xl p-2 sm:p-3 text-[#1A1A1A]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button
              onClick={goToPreviousWeek}
              className="p-1 hover:bg-stone-100 rounded-full transition-colors"
              aria-label="Previous week"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goToThisWeek}
              className="text-xs sm:text-sm font-medium px-2 py-1 hover:bg-stone-100 rounded-md transition-colors min-w-[110px] text-center"
              title="Jump to this week"
            >
              {formatWeekRange()}
            </button>
            <button
              onClick={goToNextWeek}
              className="p-1 hover:bg-stone-100 rounded-full transition-colors"
              aria-label="Next week"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-1">
            {!isRetreat && (
              <button
                onClick={() => setShowAdd(true)}
                className="text-[11px] sm:text-xs bg-[#C85A38] text-white px-2.5 py-1 rounded-full hover:bg-[#B85540] transition-colors flex items-center gap-1"
                data-testid="add-event-btn"
                title="Add an event"
              >
                <Plus className="w-3 h-3" />
                <span>{t("Add")}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Wrap printable area */}
      <div className="space-y-4">
        {/* Day picker + single-day list — same pattern at all screen sizes (just scales up on desktop). */}
        <div className="space-y-4" data-testid="schedule-day-stack">
          {/* Category filter */}
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="calendar-category-filter" aria-label="Filter by category">
            <button
              onClick={() => setCatFilter("all")}
              className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-medium border transition-colors ${catFilter === "all" ? "bg-[#2E2A26] border-[#2E2A26] text-white" : "bg-white border-stone-200 text-[#2E2A26]/70"}`}
              data-testid="calendar-filter-all"
            >
              {t("All")}
            </button>
            {EVENT_CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCatFilter(catFilter === c.key ? "all" : c.key)}
                className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-medium border transition-colors ${catFilter === c.key ? "bg-[#2E2A26] border-[#2E2A26] text-white" : `${c.cls} border-transparent`}`}
                data-testid={`calendar-filter-${c.key}`}
              >
                {c.emoji} {c.label}
              </button>
            ))}
          </div>
          {/* Day pills */}
          <div className="bg-white rounded-2xl border border-stone-100 p-1.5 sm:p-2">
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
              {weekDates.map((date, idx) => {
                const selected = idx === mobileDay;
                const today = isToday(date);
                return (
                  <button
                    key={idx}
                    onClick={() => setMobileDay(idx)}
                    className={`flex flex-col items-center py-1 sm:py-1.5 rounded-xl text-[10px] sm:text-xs transition-colors active:scale-[0.95] ${
                      selected
                        ? "bg-[#C85A38] text-white"
                        : "text-[#2E2A26] hover:bg-[#F4ECE2]"
                    }`}
                    data-testid={`day-pill-${idx}`}
                  >
                    <span className="text-[9px] sm:text-[10px] tracking-[0.12em]">{t(DAYS[idx].slice(0,3)).toUpperCase()}</span>
                    <span className={`text-sm sm:text-base font-medium ${today && !selected ? "text-[#C85A38]" : ""}`}>
                      {date.getDate()}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected day events */}
          {(() => {
            const date = weekDates[mobileDay];
            const dayName = t(DAYS[mobileDay]);
            const today = isToday(date);
            const allDay = TIME_SLOTS.flatMap((slot) =>
              getActivitiesForDateSlot(date, slot.id).map((a) => ({ ...a, _slot: slot })),
            );
            return (
              <div
                className={`mt-4 rounded-2xl border ${today ? "border-[#C85A38]/60 bg-[#FFF3EE]" : "border-stone-100 bg-white"} overflow-hidden`}
                data-testid={`schedule-day-${mobileDay}`}
              >
                <div className={`px-3 sm:px-4 py-2 sm:py-2.5 flex items-baseline justify-between ${today ? "bg-[#C85A38]/10" : "bg-[#FBF7F1]"}`}>
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`font-medium ${today ? "text-[#C85A38]" : "text-[#2E2A26]"}`}
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.2rem" }}
                    >
                      {dayName}
                    </span>
                    {today && (
                      <span className="text-[9px] uppercase tracking-wider text-[#C85A38] font-medium">{t("Today")}</span>
                    )}
                  </div>
                  <span className="text-[11px] text-[#2E2A26]/55">{formatDateHeader(date)}</span>
                </div>
                {allDay.length === 0 ? (
                  <div className="px-3 py-6 text-[11px] sm:text-xs text-[#2E2A26]/35 italic text-center">{t("Nothing on the calendar yet — tap + to add")}</div>
                ) : (
                  <div className="px-2 py-1.5 sm:px-3 sm:py-2 space-y-1 sm:space-y-1.5">
                    {allDay.map((a, ai) => (
                      <button
                        key={a.id + ai}
                        onClick={() => openEventDetail(a)}
                        className={`w-full text-left rounded-xl px-2.5 py-2 sm:px-3 sm:py-2.5 flex items-center gap-2.5 sm:gap-3 transition-colors ${
                          a.type === "espresso"
                            ? "bg-[#8C9A7C]/25 hover:bg-[#8C9A7C]/40"
                            : a.type === "specific"
                            ? "bg-[#C85A38]/15 hover:bg-[#C85A38]/25"
                            : "bg-[#F0EDE3] hover:bg-[#E8E4D0]"
                        } ${a.i_am_going && !isRetreat ? "ring-1 ring-[#1A1A1A]" : ""}`}
                        data-testid={`event-row-${a.id}`}
                      >
                        <span className="text-xl sm:text-2xl">{a.emoji || "•"}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] sm:text-sm text-[#2E2A26] font-medium leading-tight line-clamp-2 break-words">
                            {a.name}
                          </div>
                          <div className="text-[10px] sm:text-[11px] text-[#2E2A26]/55 mt-0.5 truncate">
                            {a.time}
                            {a.time && a.location ? " · " : ""}
                            {a.location && `📍 ${a.location}`}
                            {a.created_by_name ? ` · ${t("by")} ${a.created_by_name.split(" ")[0]}${a.co_host_name ? ` & ${a.co_host_name.split(" ")[0]}` : ""}` : ""}
                            {!isRetreat && a.attendee_count > 0 ? ` · 👥 ${a.attendee_count}` : ""}
                          </div>
                          <CategoryTags categories={a.categories} testid={`event-row-cats-${a.id}`} />
                        </div>
                        {(a.images || [])[0] && (
                          <img
                            src={resolveImg(a.images[0])}
                            alt=""
                            className="w-9 h-9 rounded-lg object-cover flex-shrink-0 border border-stone-100"
                            data-testid={`event-row-thumb-${a.id}`}
                          />
                        )}
                        {a.status === "pending" && (
                          <span className="text-[10px] sm:text-[11px] text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" data-testid="event-pending-badge">Pending</span>
                        )}
                        {a.status !== "pending" && !isRetreat && (
                          <span
                            role="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (rsvpBusyId !== a.id) toggleRSVP(a);
                            }}
                            className={`text-[10px] sm:text-[11px] rounded-full px-2.5 py-1 font-medium transition-colors flex-shrink-0 ${
                              a.i_am_going
                                ? "bg-[#1A1A1A] text-white"
                                : "bg-white border border-stone-200 text-[#1A1A1A]/70 hover:border-[#C85A38]/50"
                            } ${rsvpBusyId === a.id ? "opacity-50" : ""}`}
                            data-testid={`quick-rsvp-${a.id}`}
                          >
                            {a.i_am_going ? t("✓ In") : t("In?")}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>


      {/* Legend */}
      <div className="flex gap-4 justify-center text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-[#F0EDE3]"></div>
          <span className="text-[#1A1A1A]/60">{t("Weekly Recurring")}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-[#C85A38]/20"></div>
          <span className="text-[#1A1A1A]/60">{t("Special Event")}</span>
        </div>
      </div>

      {/* Espresso Talks This Week */}
      {espressoTalks.length > 0 && (
        <div className="bg-[#F0EDE3] rounded-xl p-5">
          <h4 className="font-medium text-[#1A1A1A] text-sm mb-3">☕ Espresso Talks This Week</h4>
          <div className="space-y-2">
            {espressoTalks.map((talk, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-white rounded-xl p-3">
                <span className="text-xl">☕</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A] truncate">{talk.topic}</p>
                  <p className="text-xs text-[#1A1A1A]/60">
                    {new Date(talk.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} at {talk.time} • {talk.location}
                  </p>
                </div>
                <span className="text-xs text-[#8C9A7C]">by {talk.host_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Floating add-event button — portaled so position:fixed survives transformed ancestors.
          Hidden for retreat guests: the programme is set by the host/admin. */}
      {!isRetreat && !showAdd && !eventSheet && createPortal((
        <button
          onClick={() => setShowAdd(true)}
          className="fixed right-4 z-[60] w-14 h-14 rounded-full bg-[#C85A38] text-white shadow-lg shadow-[#C85A38]/30 flex items-center justify-center hover:bg-[#B85540] active:scale-[0.92] transition-transform"
          style={{ bottom: "calc(84px + env(safe-area-inset-bottom))" }}
          aria-label="Add an event"
          data-testid="add-event-fab"
        >
          <Plus className="w-6 h-6" strokeWidth={2} />
        </button>
      ), document.body)}

      {/* Event detail sheet — portaled to document.body to escape any ancestor with `transform`
          (e.g. .animate-fade-in on the parent page) that would otherwise break position:fixed.
          Sits ABOVE the bottom nav on mobile (items-end + safe-area padding). */}
      {eventSheet && createPortal((
        <div
          className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setEventSheet(null)}
        >
          <div
            className="bg-white rounded-t-3xl sm:rounded-xl w-full sm:max-w-md max-h-[90vh] sm:max-h-[85vh] shadow-xl animate-slide-up flex flex-col relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="event-detail-sheet"
          >
            {/* Sticky close button — always reachable in the corner */}
            <button
              onClick={() => setEventSheet(null)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm hover:bg-stone-100 text-[#1A1A1A]/60 hover:text-[#1A1A1A] flex items-center justify-center shadow-sm"
              aria-label="Close"
              data-testid="event-detail-close-btn"
            >
              ✕
            </button>

            {/* Single scrollable content area — everything (header, description, attendees) scrolls together */}
            <div className="overflow-y-auto flex-1 overscroll-contain">
              {/* Header */}
              <div className="p-5 pr-12 border-b border-stone-100">
                <div className="flex items-start gap-3">
                  <span className="text-3xl flex-shrink-0">{eventSheet.activity.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-xl text-[#1A1A1A] leading-tight break-words">
                      {eventSheet.activity.name}
                    </p>
                    <p className="text-xs text-[#1A1A1A]/60 mt-1.5 flex items-center gap-x-3 gap-y-1 flex-wrap">
                      {eventSheet.activity.time && <span>🕒 {eventSheet.activity.time}</span>}
                      {eventSheet.activity.location && <span>📍 {eventSheet.activity.location}</span>}
                      {eventSheet.activity.created_by_name && (
                        <span data-testid="event-detail-creator">
                          👤 {t("by")} {eventSheet.activity.created_by_name}
                          {eventSheet.activity.co_host_name && <span data-testid="event-detail-cohost"> &amp; {eventSheet.activity.co_host_name}</span>}
                        </span>
                      )}
                      {eventSheet.activity.type === "specific" && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-[#C85A38]">{t("One-off")}</span>
                      )}
                    </p>
                    <CategoryTags categories={eventSheet.activity.categories} size="sm" testid="event-detail-cats" />
                    {eventSheet.activity.description && (
                      <p className="text-sm text-[#1A1A1A]/75 mt-3 leading-relaxed whitespace-pre-line break-words">
                        {eventSheet.activity.description}
                      </p>
                    )}
                    {(eventSheet.activity.images || []).length > 0 && (
                      <div className="flex flex-col gap-2 mt-3">
                        {eventSheet.activity.images.slice(0, 2).map((img, i) => (
                          <a
                            key={i}
                            href={resolveImg(img)}
                            target="_blank"
                            rel="noreferrer"
                            className="block"
                          >
                            <img
                              src={resolveImg(img)}
                              alt={`${eventSheet.activity.name} photo ${i + 1}`}
                              className="w-full h-auto max-h-[55vh] object-contain rounded-xl border border-stone-100 bg-stone-50"
                              data-testid={`event-detail-image-${i}`}
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Attendees — retreat guests are all in, so no sign-up list for them */}
              {!isRetreat && (
              <div className="px-5 py-4">
                <p className="text-xs font-medium uppercase tracking-wider text-[#1A1A1A]/55 mb-3">
                  {t("Going")} ({eventSheet.attendees.length})
                </p>
                {eventSheet.loading ? (
                  <p className="text-sm text-[#1A1A1A]/60">{t("Loading…")}</p>
                ) : eventSheet.attendees.length === 0 ? (
                  <p className="text-sm italic text-[#1A1A1A]/55">{t("Nobody's confirmed yet — be the first 🙌")}</p>
                ) : (
                  <ul className="space-y-2">
                    {eventSheet.attendees.map((a) => (
                      <li key={a.user_id} className="flex items-center gap-3 bg-stone-50 rounded-xl p-2.5">
                        {a.user_avatar ? (
                          <img
                            src={a.user_avatar.startsWith("/") ? `${API.replace(/\/api$/, "")}${a.user_avatar}` : a.user_avatar}
                            alt={a.user_name}
                            className="w-9 h-9 rounded-full object-cover bg-stone-200 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-[#F0EDE3] flex items-center justify-center text-sm font-serif text-[#1A1A1A] flex-shrink-0">
                            {(a.user_name || "?").charAt(0)}
                          </div>
                        )}
                        <span className="text-sm font-medium text-[#1A1A1A] truncate">{a.user_name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              )}
            </div>

            {/* Sticky RSVP footer — stays visible while scrolling (hidden for retreat guests: no sign-up needed) */}
            {!isRetreat && (
            <div className="border-t border-stone-100 bg-white p-4 space-y-2 flex-shrink-0" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
              {eventSheet.activity.status === "pending" ? (
                user?.role === "admin" ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => approvePendingEvent(eventSheet.activity.id, true)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-medium bg-[#8C9A7C] text-white hover:bg-[#7C8A6C] transition-colors"
                      data-testid="event-detail-approve-btn"
                    >
                      <Check className="w-4 h-4" /> Approve
                    </button>
                    <button
                      onClick={() => approvePendingEvent(eventSheet.activity.id, false)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-medium bg-white border border-stone-200 text-[#C85A38] hover:bg-stone-50 transition-colors"
                      data-testid="event-detail-decline-btn"
                    >
                      ✕ Decline
                    </button>
                  </div>
                ) : (
                <div className="w-full text-center text-xs text-amber-700 bg-amber-50 rounded-full py-3" data-testid="event-pending-note">
                  ⏳ Waiting for Steve's approval — the house gets invited once it's live
                </div>
                )
              ) : (
              <button
                onClick={() => toggleRSVP(eventSheet.activity)}
                disabled={rsvpBusyId === eventSheet.activity.id || !headers}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-full font-medium transition-colors disabled:opacity-50 ${
                  eventSheet.activity.i_am_going
                    ? "bg-[#1A1A1A] text-white hover:bg-[#2D3025]"
                    : "bg-[#C85A38] text-white hover:bg-[#B85540]"
                }`}
                data-testid="event-detail-rsvp-btn"
              >
                <Check className="w-4 h-4" />
                {eventSheet.activity.i_am_going ? t("You're going — tap to cancel") : t("I'm in!")}
              </button>
              )}
              {(user?.role === "admin" ||
                (eventSheet.activity.type === "specific" &&
                 (eventSheet.activity.created_by_id === user?.id || eventSheet.activity.co_host_id === user?.id))) && (
                <div className="flex gap-2">
                  {eventSheet.activity.type === "specific" && (
                    <button
                      onClick={() => openEdit(eventSheet.activity)}
                      className="flex-1 flex items-center justify-center gap-2 py-1.5 rounded-full text-xs text-[#1A1A1A]/70 hover:bg-stone-50"
                      data-testid="event-detail-edit-btn"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit event
                    </button>
                  )}
                  <button
                    onClick={() => deleteMyEvent(eventSheet.activity.id)}
                    className="flex-1 flex items-center justify-center gap-2 py-1.5 rounded-full text-xs text-[#C85A38] hover:bg-stone-50"
                    data-testid="event-detail-delete-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete event
                  </button>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      ), document.body)}

      {/* Add Event dialog */}
      <Dialog open={showAdd} onOpenChange={(v) => { if (!creating) { setShowAdd(v); if (!v) { setOverlapPrompt(null); resetEventForm(); } } }}>
        <DialogContent className="max-w-md p-0 overflow-hidden max-h-[92vh] flex flex-col" data-testid="add-event-dialog">
          {/* Header */}
          <div className="px-5 pt-5 pb-3 bg-[#1A1A1A] text-white flex-shrink-0">
            <DialogHeader className="space-y-1 text-left">
              <DialogTitle className="font-serif text-2xl text-white">{editingId ? "Edit your event" : "Add an event"}</DialogTitle>
              <DialogDescription className="text-xs text-white/70">
                {editingId ? "Changes go live straight away." : "Everyone in the house will see it on the calendar."}
              </DialogDescription>
            </DialogHeader>
          </div>
          {/* Body */}
          <div className="px-5 py-4 space-y-3.5 bg-[#FBF7F1] overflow-y-auto flex-1 min-h-0">
            <div>
              <Label className="text-[11px] text-[#2E2A26]/60">Name *</Label>
              <Input
                value={newEvent.name}
                onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                placeholder="e.g. Sunset yoga"
                className="mt-1 h-10 rounded-lg border-stone-200 bg-white"
                data-testid="add-event-name"
              />
            </div>
            <div>
              <Label className="text-[11px] text-[#2E2A26]/60">{t("What kind of thing is it?")} <span className="text-[#2E2A26]/40">(1–3)</span></Label>
              <div className="mt-1.5">
                <CategoryPicker
                  value={newEvent.categories}
                  onChange={setCategories}
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-[#2E2A26]/60">{t("Icon")}</Label>
              <div className="mt-1.5">
                <EmojiPicker value={newEvent.emoji} onChange={(em) => { setEmojiTouched(true); setNewEvent({ ...newEvent, emoji: em }); }} />
              </div>
            </div>
            {communityProfiles.filter((p) => p.id !== user?.id).length > 0 && (
              <div>
                <Label className="text-[11px] text-[#2E2A26]/60">Co-host <span className="text-[#2E2A26]/40">(optional)</span></Label>
                <select
                  value={newEvent.co_host_id}
                  onChange={(e) => setNewEvent({ ...newEvent, co_host_id: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
                  data-testid="add-event-cohost"
                >
                  <option value="">— Just me —</option>
                  {communityProfiles
                    .filter((p) => p.id !== user?.id)
                    .map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
                <p className="text-[10px] text-[#2E2A26]/45 mt-1">They'll get a heads-up and can edit the event too.</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-[#2E2A26]/60">Date *</Label>
                <Input
                  type="date"
                  value={newEvent.date}
                  min={todayISO}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  className="mt-1 h-10 rounded-lg border-stone-200 bg-white"
                  data-testid="add-event-date"
                />
              </div>
              <div>
                <Label className="text-[11px] text-[#2E2A26]/60">Time *</Label>
                <Input
                  type="time"
                  value={newEvent.time}
                  onChange={(e) => {
                    const t = e.target.value;
                    setNewEvent({ ...newEvent, time: t, time_slot: pickTimeSlot(t) });
                  }}
                  className="mt-1 h-10 rounded-lg border-stone-200 bg-white"
                  data-testid="add-event-time"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-[#2E2A26]/60">Time of day</Label>
                <select
                  value={newEvent.time_slot}
                  onChange={(e) => setNewEvent({ ...newEvent, time_slot: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
                  data-testid="add-event-time-slot"
                >
                  <option value="morning">Morning (8-12)</option>
                  <option value="midday">Midday (12-15)</option>
                  <option value="afternoon">Afternoon (15-18)</option>
                  <option value="evening">Evening (18+)</option>
                </select>
              </div>
              <div>
                <Label className="text-[11px] text-[#2E2A26]/60">Where</Label>
                <select
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
                  data-testid="add-event-location"
                >
                  <option value="">— Pick a spot —</option>
                  {EVENT_LOCATIONS.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-[#2E2A26]/60">Details (optional)</Label>
              <Textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                placeholder="What's it about? Anything to bring?"
                className="mt-1 min-h-[70px] rounded-lg border-stone-200 bg-white text-sm"
                data-testid="add-event-description"
              />
            </div>
            <div>
              <Label className="text-[11px] text-[#2E2A26]/60">Photos (up to 2, optional)</Label>
              <div className="mt-1 flex gap-2">
                {newEvent.images.map((img, i) => (
                  <div key={i} className="relative w-16 h-16 flex-shrink-0">
                    <img
                      src={resolveImg(img)}
                      alt=""
                      className="w-16 h-16 object-cover rounded-lg border border-stone-200"
                      data-testid={`add-event-image-preview-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => setNewEvent({ ...newEvent, images: newEvent.images.filter((_, j) => j !== i) })}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#2E2A26] text-white flex items-center justify-center shadow"
                      data-testid={`add-event-image-remove-${i}`}
                      aria-label="Remove photo"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {newEvent.images.length < 2 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImg}
                    className="w-16 h-16 rounded-lg border border-dashed border-stone-300 bg-white text-[#2E2A26]/40 hover:text-[#C85A38] hover:border-[#C85A38]/50 flex flex-col items-center justify-center transition-colors disabled:opacity-50"
                    data-testid="add-event-image-btn"
                  >
                    {uploadingImg ? (
                      <span className="text-[10px]">Uploading…</span>
                    ) : (
                      <>
                        <ImagePlus className="w-5 h-5" />
                        <span className="text-[9px] mt-0.5">Add photo</span>
                      </>
                    )}
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageUpload}
                  data-testid="add-event-image-input"
                />
              </div>
            </div>

          </div>
          {/* Footer */}
          <div className="px-5 py-3 bg-white border-t border-stone-100 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end flex-shrink-0">
            <Button
              variant="ghost"
              onClick={() => { setShowAdd(false); resetEventForm(); }}
              disabled={creating}
              className="text-[#2E2A26]/70 hover:bg-stone-50"
              data-testid="add-event-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => submitNewEvent(false)}
              disabled={creating}
              className="bg-[#C85A38] hover:bg-[#B85540] text-white rounded-full px-5 h-10"
              data-testid="add-event-submit"
            >
              {creating ? "Saving…" : editingId ? "Save changes" : "Add to calendar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Overlap warning */}
      <Dialog open={!!overlapPrompt} onOpenChange={(v) => !v && setOverlapPrompt(null)}>
        <DialogContent className="max-w-sm" data-testid="overlap-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#2E2A26]">Heads up — same time as…</DialogTitle>
            <DialogDescription className="text-xs text-[#2E2A26]/60">
              You can still post it — just so you know.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-2 text-sm text-[#2E2A26]">
            {(overlapPrompt?.events || []).map((ev, i) => (
              <li key={i} className="bg-[#F4ECE2] rounded-xl px-3 py-2">
                <div className="font-medium">{ev.name}</div>
                <div className="text-xs text-[#2E2A26]/60">
                  {ev.time}{ev.location ? ` · ${ev.location}` : ""}
                </div>
              </li>
            ))}
          </ul>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOverlapPrompt(null)} disabled={creating}>
              Pick another time
            </Button>
            <Button
              onClick={() => submitNewEvent(true)}
              disabled={creating}
              className="bg-[#C85A38] hover:bg-[#B85540] text-white"
              data-testid="overlap-confirm"
            >
              {creating ? "Saving…" : "Post anyway"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActivitiesTab;
